import React, { useState } from 'react';
import { ProductOptionInput, ProductVariant, ProductVariantInput, ProductVariantOptionInput } from '@shopify-multiseller/graphql/partner/types';
import { DndContext, DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { PhotoIcon, TrashIcon } from '@heroicons/react/24/solid';
import { useFormikContext, FormikErrors } from 'formik';
import { Note } from '../../../components/primitives';
import { Button } from '../../../components/primitives';
import { FormInput } from '../../../components/form';
import { Badge } from '../../../components/primitives';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../components/primitives';
import { GripVertical } from 'lucide-react';
import { SectionCard } from '../../../components/SectionCard';
import { useImages } from '../../../components/ProductImage/ImagesProvider';
import { useProductOptionEditor } from '../../../hooks/useProductOptionEditor';
import { useProductVariantEditor } from '../../../hooks/useProductVariantEditor';
import { IProductInitialValuesProps } from '../types';
import { VariantSelectionModal } from '../../../components/VariantSelectionModal';
import { v4 as uuid } from 'uuid';
import { Checkbox } from '../../../components/primitives';

type VariantFormErrors = FormikErrors<ProductVariantInput & { showSku: boolean; isNew: boolean }>;

const OptionItem: React.FC<{ option: ProductOptionInput, onEdit: (_option: ProductOptionInput) => void, onDelete: (_id: string) => void, disabled?: boolean, showHandle?: boolean }> = ({ option, onEdit, onDelete, disabled, showHandle }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: String(option.name),
    disabled: disabled || !showHandle,
  });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 50 : undefined }}
      {...(showHandle && !disabled ? { ...attributes, ...listeners } : {})}
      className={`flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 ${isDragging ? 'shadow-lg' : ''}`}
    >
      {showHandle && (
        <GripVertical className={`h-4 w-4 shrink-0 cursor-grab text-muted-foreground ${disabled ? 'text-muted-foreground/40' : ''}`} />
      )}
      {/* The option name and its values on one line: two lines per option
          pushed the combinations table below the fold on a three-option
          product, which is exactly when you most want to see it. */}
      <span className="shrink-0 text-sm font-medium text-foreground">{option.name}</span>
      <div className="flex flex-wrap items-center gap-1.5">
        {option.values.map((value) => (
          <Badge variant="neutral" key={value}>{value}</Badge>
        ))}
      </div>
      {!disabled && (
        <div className={`ml-auto flex shrink-0 items-center gap-2 ${isDragging ? 'hidden' : ''}`}>
          <Button variant="outline" size="sm" data-testid="option-edit-button" onClick={() => onEdit(option)}>Edit</Button>
          <button type="button" aria-label={`Delete ${option.name}`} onClick={() => onDelete(option.id)}>
            <TrashIcon className="h-4 w-4 text-muted-foreground hover:text-foreground" />
          </button>
        </div>
      )}
    </div>
  );
};

const getUniqueErrorMessages = (errors: any): string[] => {
  if (!errors?.variants) return [];

  // Collect all error messages from variants
  const messages = Object.values(errors.variants)
    .flatMap((variantError: any) =>
      Object.values(variantError || {})
    )
    .filter((message): message is string => typeof message === 'string');

  // Remove duplicates
  return [...new Set(messages)];
};

const VariantRow: React.FC<{
  variant: ProductVariantInput,
  options: ProductOptionInput[],
  onEdit: (_variant: ProductVariantInput, _viewOnly?: boolean) => void,
  onDelete: (_id: string) => void,
  error: boolean,
  index: number,
  /** Server-side stock for existing variants (0 for new ones). */
  storedQuantity: number,
  isDigital: boolean,
  hideErrorMessages?: boolean,
  disabled?: boolean
}> = ({ variant, onDelete, options, onEdit, error, index, storedQuantity, isDigital, hideErrorMessages, disabled }) => {
  const variantOptions = generateOptionsString(variant, options);
  const { images } = useImages();
  const image = images.find(i => i.id === variant.imageId);
  const { setFieldValue, values, errors } = useFormikContext<IProductInitialValuesProps>();
  const [showUnlimitedCheckbox, setShowUnlimitedCheckbox] = useState(!variant.trackQuantity);
  const currentVariant = values.variants[index];
  const variantErrors = errors.variants?.[index] as VariantFormErrors;

  // Mirror PricingInventoryFields: the input always displays the RESULTING
  // stock. New variants store it directly (initialQuantity); existing ones
  // store the delta from the server-side quantity (quantityAdjustment) —
  // previously this input showed the raw delta as if it were the stock.
  const adjustment = typeof currentVariant?.quantityAdjustment === 'number' ? currentVariant.quantityAdjustment : undefined;
  const displayedQuantity = variant.isNew
    ? currentVariant?.initialQuantity || 0
    : storedQuantity + (adjustment ?? 0);

  const handleQuantityChange = (value: string) => {
    const numValue = parseInt(value, 10);
    if (isNaN(numValue)) return;
    if (variant.isNew) {
      setFieldValue(`variants.${index}.initialQuantity`, numValue);
    } else {
      setFieldValue(`variants.${index}.quantityAdjustment`, numValue - storedQuantity);
    }
  };

  const quantityInput = (
    <FormInput
      type="number"
      name={`variants.${index}.${variant.isNew ? 'initialQuantity' : 'quantity'}`}
      aria-label="Quantity"
      min={0}
      step={1}
      value={displayedQuantity}
      onChange={(e) => handleQuantityChange(e.target.value)}
      className={
        error && (variantErrors?.initialQuantity || variantErrors?.quantityAdjustment)
          ? 'border-destructive'
          : ''
      }
      hideError={hideErrorMessages}
      disabled={disabled}
    />
  );

  const handleUnlimitedClick = (e: React.MouseEvent) => {
    if (disabled) return;
    e.preventDefault();
    setShowUnlimitedCheckbox(true);
    setFieldValue(`variants.${index}`, {
      ...currentVariant,
      trackQuantity: false,
      [variant.isNew ? 'initialQuantity' : 'quantityAdjustment']: undefined
    });
  };

  return (
    <TableRow className="hover:bg-transparent">
      <TableCell className="w-12">
        {!image ? (
          <div className="flex h-9 w-9 items-center justify-center rounded border border-border bg-muted/40">
            <PhotoIcon className="h-4 w-4 text-muted-foreground/50" aria-hidden="true" />
          </div>
        ) : (
          <div className="h-9 w-9 overflow-hidden rounded border border-border bg-muted">
            <img src={image?.thumbnailUrl} alt="" className="h-full w-full object-cover" />
          </div>
        )}
      </TableCell>

      <TableCell>
        <span className="text-sm font-medium text-foreground">{variantOptions}</span>
        {variant.isNew && (
          <span className="ml-2 align-middle">
            <Badge variant="warning">New</Badge>
          </span>
        )}
      </TableCell>

      <TableCell className="w-[132px]">
        <FormInput
          money
          cents
          name={`variants.${index}.price`}
          aria-label="Price"
          prefix="$"
          className={error && variantErrors?.price ? 'border-destructive' : ''}
          hideError={hideErrorMessages}
          disabled={disabled}
        />
      </TableCell>

      <TableCell className="w-[132px]">
        {isDigital && showUnlimitedCheckbox ? (
          <div className="flex items-center gap-2">
            <Checkbox
              id={`unlimited-${variant.id}`}
              checked={!values.variants[index].trackQuantity}
              onCheckedChange={(checked) => {
                if (disabled) return;
                const isUnlimited = checked === true;
                setFieldValue(`variants.${index}`, {
                  ...values.variants[index],
                  trackQuantity: !isUnlimited,
                  [variant.isNew ? 'initialQuantity' : 'quantityAdjustment']: isUnlimited ? undefined : 0
                });
                if (!isUnlimited) {
                  setShowUnlimitedCheckbox(false);
                }
              }}
              disabled={disabled}
            />
            <label htmlFor={`unlimited-${variant.id}`} className="cursor-pointer select-none text-sm text-foreground">
              unlimited
            </label>
          </div>
        ) : (
          <>
            {quantityInput}
            {isDigital && !disabled && (
              <button
                type="button"
                className="mt-1 text-xs text-primary hover:underline"
                onClick={handleUnlimitedClick}
              >
                unlimited
              </button>
            )}
          </>
        )}
      </TableCell>

      <TableCell className="text-sm text-muted-foreground">{variant.sku || '—'}</TableCell>

      <TableCell className="w-[110px] text-right">
        {!disabled ? (
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              data-testid="variant-edit-button"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(variant);
              }}
            >
              Edit
            </Button>
            <button
              type="button"
              aria-label={`Delete ${variantOptions}`}
              onClick={(e) => {
                e.stopPropagation();
                onDelete(variant.id);
              }}
            >
              <TrashIcon className="h-4 w-4 text-muted-foreground hover:text-foreground" />
            </button>
          </div>
        ) : (
          <Button
            variant="outline"
            size="sm"
            data-testid="variant-view-button"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(variant, true);
            }}
          >
            View
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
};

export interface IProductVariantsSectionProps {
  disabled?: boolean;
  trackQuantity: boolean;
  isPhysicalProduct: boolean;
  variants: ProductVariant[];
}

export const ProductVariantsSection: React.FC<IProductVariantsSectionProps> = ({ trackQuantity, isPhysicalProduct, variants, disabled }) => {
  const { values, setFieldValue, errors, submitCount } = useFormikContext<IProductInitialValuesProps>();
  const [showVariantModal, setShowVariantModal] = useState(false);
  const options = values.options;
  const hasVariants = values.variants && values.variants.length > 0;
  const hasOptions = options && options.length > 0;
  const combinationCount = options?.reduce((acc, option) => acc * option.values.length, 1) ?? 0;
  const hasAllVariants = hasOptions && hasVariants && values.variants?.length === combinationCount;

  const productVariantEditor = useProductVariantEditor();
  const productOptionEditor = useProductOptionEditor();
  const dragSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  // If no product type is selected, don't show the variants section
  if (!values.productType) {
    return null;
  }

  // If the page is disabled and there are no options, don't show the variants section
  if (disabled && !options?.length) {
    return null;
  }

  const handleEditOption = (option: ProductOptionInput) => {
    productOptionEditor.open({
      option: option || undefined,
      options: values.options || [],
      onSave: handleSaveExistingOption
    });
  };

  const handleSaveNewOption = (option: ProductOptionInput) => {
    const _options = options || [];
    setFieldValue('options', [..._options, option]);
    productOptionEditor.close();
  }

  const handleSaveExistingOption = (option: ProductOptionInput) => {
    options && setFieldValue('options', options.map(o => o.id === option.id ? option : o));
    productOptionEditor.close();
  }

  const handleEditVariant = (variant: ProductVariantInput, viewOnly: boolean = false) => {
    const currentVariant: ProductVariant | undefined = variants?.find((v) => v.id === variant?.id);

    productVariantEditor.open({
      variant: variant,
      productId: values.id,
      options: values.options || [],
      variants: values.variants,
      trackQuantity: trackQuantity,
      quantity: currentVariant?.quantity || 0,
      productType: values.productType!,
      disabled: disabled || viewOnly,
      onSave: handleSaveExistingVariant
    });
  }

  const handleDeleteVariant = (id: string) => {
    const _variants = values.variants || [];

    // If we're about to delete the last variant
    if (_variants.length === 1) {
      // Keep the variant but clear its options
      setFieldValue('variants', [{
        ..._variants[0],
        variantOptions: []
      }]);
      // Clear all options
      setFieldValue('options', []);
    } else {
      // Normal deletion for non-last variant
      setFieldValue('variants', _variants.filter(v => v.id !== id));
    }
  };

  const handleDeleteOption = (id: string) => {
    const _options = options || [];
    setFieldValue('options', _options.filter(o => o.id !== id));
  }

  const handleAddOption = () => {
    productOptionEditor.open({
      option: undefined,
      options: values.options || [],
      onSave: handleSaveNewOption
    });
  }

  const handleAddVariant = (variantOptions: ProductVariantOptionInput[]) => {
    const newVariant: ProductVariantInput = {
      id: uuid(),
      price: 0,
      sku: '',
      trackQuantity: isPhysicalProduct,
      isNew: true,
      initialQuantity: 0,
      variantOptions: variantOptions
    };
    setFieldValue('variants', [...values.variants, newVariant]);
  };

  const handleSaveExistingVariant = (variant: ProductVariantInput) => {
    setFieldValue('variants', values.variants.map(v => v.id === variant.id ? variant : v));
    productVariantEditor.close();
  }

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id || !options) return;

    const oldIndex = options.findIndex((o) => String(o.name) === String(active.id));
    const newIndex = options.findIndex((o) => String(o.name) === String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;

    const reorderedOptions = arrayMove(Array.from(options), oldIndex, newIndex);
    reorderedOptions.forEach((option, index) => {
      option.position = index;
    });

    setFieldValue('options', reorderedOptions);
  };

  // Three is the ceiling Shopify imposes, so the action disappears at the
  // limit rather than failing when used.
  const canAddOption = !disabled && (options?.length ?? 0) < 3;
  const missingCombinations = hasOptions ? combinationCount - (values.variants?.length ?? 0) : 0;
  // Which ones, not just how many. A seller told "1 combination not created
  // yet" still has to work out which of six it is by reading the table; the
  // design names it, and naming it is the difference between a notice and an
  // instruction. Two at most, because past that the table is the better place
  // to look.
  const missingNames = namesOfMissingCombinations(options ?? [], values.variants ?? []).slice(0, 2);

  return (
    <SectionCard
      title="Variations"
      testId="product-variants-section"
      trailing={
        canAddOption && hasOptions ? (
          <Button variant="ghost" size="sm" onClick={handleAddOption}>
            + Add option
          </Button>
        ) : undefined
      }
    >
      {!options?.length ? (
        <div className="space-y-4">
          <Note>
            Options are the things a buyer chooses — size, colour, finish. Add one and every
            combination gets its own price, quantity and SKU below.
          </Note>
          <Button disabled={disabled} variant="outline" size="sm" onClick={handleAddOption}>
            + Add option
          </Button>
        </div>
      ) : (
        <div className="space-y-2 rounded-lg border border-border bg-muted/40 p-2.5">
          <DndContext sensors={dragSensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={options.map((o) => String(o.name))} strategy={verticalListSortingStrategy}>
              {options.map((option) => (
                <OptionItem
                  disabled={disabled}
                  key={option.name}
                  option={option}
                  onEdit={handleEditOption}
                  onDelete={handleDeleteOption}
                  showHandle={options.length > 1}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      )}

      {hasVariants && hasOptions && (
        <div className="mt-5">
          {/* Add consolidated error messages panel */}
          {submitCount > 0 && errors.variants && (
            <div className="mb-4">
              <Note tone="critical" title="Fix these before saving">
                <ul className="mt-1 list-inside list-disc">
                  {getUniqueErrorMessages(errors).map((message, index) => (
                    <li key={index}>{message}</li>
                  ))}
                </ul>
              </Note>
            </div>
          )}
          {/*
            One row per combination, with price and quantity editable in place.
            Each variant previously occupied a 113px block with its own labels
            repeated — six variants filled a screen, and the prices you were
            comparing were nowhere near each other.
          */}
          <div className="overflow-hidden rounded-lg border border-border">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-12" />
                  <TableHead>Variation</TableHead>
                  <TableHead className="w-[132px]">Price</TableHead>
                  <TableHead className="w-[132px]">Qty</TableHead>
                  <TableHead>SKU</TableHead>
                  <TableHead className="w-[110px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {values.variants.map((variant, index) => {
                  const isVariantErroring = errors.variants && errors.variants[index] && submitCount > 0 ? true : false;
                  return (
                    <VariantRow
                      error={isVariantErroring}
                      options={options}
                      key={variant.id}
                      variant={variant}
                      index={index}
                      storedQuantity={variants?.find((v) => v.id === variant.id)?.quantity ?? 0}
                      onDelete={handleDeleteVariant}
                      onEdit={handleEditVariant}
                      isDigital={!isPhysicalProduct}
                      hideErrorMessages={hasOptions}
                      disabled={disabled}
                    />
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {!disabled && !hasAllVariants && (
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <Button disabled={disabled} variant="ghost" size="sm" onClick={() => setShowVariantModal(true)}>
                + Add variation
              </Button>
              {/* Says how many combinations the options allow but nobody has
                  created — the previous UI offered the button with no hint
                  that anything was outstanding. */}
              <span className="text-xs text-muted-foreground">
                {`${
                  missingCombinations === 1
                    ? '1 combination not created yet'
                    : `${missingCombinations} combinations not created yet`
                }${missingNames.length > 0 ? `: ${missingNames.join(', ')}` : ''}`}
              </span>
            </div>
          )}
        </div>
      )}

      <VariantSelectionModal
        open={showVariantModal}
        onClose={() => setShowVariantModal(false)}
        options={options || []}
        existingVariants={values.variants}
        onSelect={handleAddVariant}
      />
    </SectionCard>
  );
};

/**
 * The option combinations that have no variation yet, in the order the options
 * are set — "Blackened steel / Large".
 *
 * Built by walking the full matrix and removing the ones a variation already
 * covers, so it stays right when a variation is deleted as well as when one is
 * added. Options carry positions, and both this and the table sort by them, so
 * a combination is named the same way wherever it appears.
 */
const namesOfMissingCombinations = (
  options: ProductOptionInput[],
  variants: ProductVariantInput[],
): string[] => {
  if (options.length === 0) return [];

  const ordered = [...options].sort((a, b) => a.position - b.position);
  const matrix = ordered.reduce<string[][]>(
    (rows, option) => rows.flatMap((row) => option.values.map((value) => [...row, value])),
    [[]],
  );

  const taken = new Set(
    variants.map((variant) => generateOptionsString(variant, options)).filter(Boolean),
  );
  return matrix.map((row) => row.join(' / ')).filter((name) => !taken.has(name));
};

const generateOptionsString = (variant: ProductVariantInput, options: ProductOptionInput[]): string => {
  const mappedOptions = variant.variantOptions
    ? variant.variantOptions.map(vOption => {
      const option = options.find(opt => opt.id === vOption.optionId);
      return option ? { ...vOption, position: option.position } : null;
    }).filter(Boolean) // Filters out any null values if not found
    : [];

  const sortedOptions = mappedOptions.sort((a, b) => {
    if (!a || !b) {
      return 0;
    }
    return a.position - b.position;
  });

  const variantOptions = sortedOptions.map((vOption) => {
    return vOption ? vOption.value : "";
  }).join(" / ");
  return variantOptions;
}
